import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GetMetaHeader, { PAGEID } from '../../Common/seoHelper';
import { getNewsletterArticleContent } from '../../../actions/mspUser';
import Brand from '../../Layout/Header/Brand';
import logo from '../../../assets/img/tomcom-dark.svg';
import unicornLogo from '../../../assets/img/tomcom-logo-unicorn-dark-bg.png';
import ArticleDetail from '../News/WeeklyNewsView/ArticleDetail';
import NotFound from '../NotFound';
import { getAuthToken, getSelectedRole } from '../../../actions/AuthUtil';
import { NEWS_PATH, resolveNewsPath } from '../../../helpers/newsNavigation';

const MOBILE_UA_REGEX = /Android|iPhone|iPad|iPod/i;

const isIpad = () => {
  const nav: any = navigator;
  const ua = nav.userAgent || '';
  const platform = nav.platform || '';
  const hasTouch = (nav.maxTouchPoints || 0) > 1 || 'ontouchstart' in window;
  if (nav.userAgentData?.platform) {
    return nav.userAgentData.platform === 'iOS' && hasTouch;
  }
  return platform === 'iPad'
    || (platform === 'MacIntel' && hasTouch)
    || /iPad/.test(ua);
};
const APP_STORE_URL = 'https://apps.apple.com/in/app/tomcom/id1633602585';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.jbi.tomcom&hl=en_IN';

const NewsletterContentPreview = () => {
  const navigate = useNavigate();
  const { id, hash } = useParams<{ id?: string; hash?: string }>();
  const [content, setContent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isiPad = isIpad();
    setIsMobile(MOBILE_UA_REGEX.test(ua) || isiPad);
    setIsIOS(/iPhone|iPad|iPod/i.test(ua) || isiPad);
    setIsAndroid(/Android/i.test(ua));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const getInstalledRelatedApps = (navigator as any).getInstalledRelatedApps;
    if (!getInstalledRelatedApps) {
      return;
    }

    getInstalledRelatedApps()
      .then((apps: any[]) => {
        setIsAppInstalled(Array.isArray(apps) && apps.length > 0);
      })
      .catch(() => {
        setIsAppInstalled(false);
      });
  }, [isMobile]);

  useEffect(() => {
    if (!id || !hash) {
      setNotFound(true);
      return;
    }

    getNewsletterArticleContent(id, hash)
      .then((result: any) => {
        if (isMountedRef.current) {
          setContent(result?.content);
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setNotFound(true);
        }
      });
  }, [id, hash]);

  // Back and Read more both go to the news home page. A signed-out reader is
  // sent to login first and lands on news once they are in (and once they have
  // picked a role, if they have more than one).
  const onGoToNews = (e: React.MouseEvent) => {
    e.preventDefault();
    if (getAuthToken()) {
      navigate(resolveNewsPath(getSelectedRole()?.name));
      return;
    }
    navigate('/login', { state: { prevPath: NEWS_PATH } });
  };

  const showAppBanner = isMobile;
  const ctaText = isAppInstalled ? 'View in app' : 'Download app';
  const viewInAppHref = `/app?type=article&id=${encodeURIComponent(id || '')}&hash=${encodeURIComponent(hash || '')}`;
  // eslint-disable-next-line no-nested-ternary
  const storeHref = isIOS ? APP_STORE_URL
    : (isAndroid ? 'market://details?id=com.jbi.tomcom' : PLAY_STORE_URL);
  const ctaHref = isAppInstalled ? viewInAppHref : storeHref;
  // const bannerSubtext = isAppInstalled ? 'Open in the app' : 'Download our app';

  const header = () => (
    <header className="main-header relative">
      <div
        className={`p-4 pl-4  md:p-8 md:pl-12 flex items-center border-b border-themered fixed ${showAppBanner ? 'top-12' : 'top-0'} right-0 left-0 z-50 transition-all bg-white`}
      >
        <a
          className="mr-3 flex items-center gap-2 text-themered text-sm font-semibold"
          href={NEWS_PATH}
          onClick={onGoToNews}
        >
          <span aria-hidden="true">←</span>
          <span>Back</span>
        </a>
        <Brand url="/" />
      </div>
    </header>
  );

  const appBanner = showAppBanner ? (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-white border-b border-gray-200 h-14 flex items-center">
      <div className="flex items-center gap-3 px-4 w-full">
        <img className="h-10 w-10 rounded" src={unicornLogo} alt="TomCom" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500">
            <img className="h-12 w-12" src={logo} alt="TomCom" />
          </div>
        </div>
        <a
          className="text-xs font-semibold text-white bg-themered px-3 py-2 rounded"
          href={ctaHref}
          rel="noreferrer"
        >
          {ctaText}
        </a>
      </div>
    </div>
  ) : null;

  if (notFound) {
    return (
      <>
        {appBanner}
        {showAppBanner && <div className="h-12" />}
        {!showAppBanner && header()}
        <NotFound showBackHomeBtn={false} />
      </>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <>
      <GetMetaHeader page={PAGEID.PAGE_ARTICLE} />
      <>
        {appBanner}
        {showAppBanner && <div className="h-12" />}
        {!showAppBanner && header()}
        <div className="relative mt-14 md:mt-20">
          <div className="pb-4 md:ml-[20%] md:mr-[20%] ml-0 mr-0">
            {
                  showAppBanner && (
                  <a
                    className="mr-3 flex items-center gap-2 text-themered text-sm font-semibold p-2"
                    href={NEWS_PATH}
                    onClick={onGoToNews}
                  >
                    <span aria-hidden="true">←</span>
                    <span>Back</span>
                  </a>
                  )
              }
            <ArticleDetail
              article={content}
              bodyContainerClass="bg-white drop-shadow-[2px_4px_6px_rgba(0,0,0,1)] rounded-[30px] ml-[4%] mr-[4%] mt-[4%] mb-[4%]"
              showShareBtn={false}
            />
            <div className="flex justify-center mt-6 ml-[4%] mr-[4%]">
              <a
                className="btn btn-red w-full sm:w-80"
                href={NEWS_PATH}
                onClick={onGoToNews}
              >
                Read More
              </a>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default NewsletterContentPreview;
